import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _style from './style.scss';
import { useTranslation } from 'react-i18next';
import { ze } from 'utils/zhEn';
import { formatCompactNumber, formatCrashPercentageString, formatEnNumber } from 'utils/format/format-number';
import { isNotNullish, isNullish } from 'utils/nullish';
import { Space, Tooltip } from 'antd';
import { ExceptionCategory, ExceptionCategoryUtil } from 'utils/exception-category';
import isEmpty from 'lodash/lang/isEmpty';
import { SummaryHeadDataType } from 'utils/constants/summary-head-data-type';
import { VmTypeInt, VmTypeUtil } from 'utils/constants/vm-type';
import { isAndroid, isAndroidOrHarmony, isIos, isPc, PlatformUtil } from 'utils/platform';
import { EnvUtil } from 'utils/env-util';
import reportEvent, { EVENT_ACTIONS } from 'utils/reportEvent';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';
import { ServerAppSettings } from 'utils/server-app-settings';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {Link} from "react-router-dom";
import DashboardFilterIcon from 'svg/v2/newcs_dashboard_overview_configured.svg';
import { ix } from 'utils/i18n-with-context';
import { ProductSummaryUtil } from 'components/exception/ProductSummaryPage/ProductSummaryUtil';
import {BillingTeamType} from 'utils/constants/billing';

function makeErrorDiscardStrategyTooltip(reportDiscardStrategy, pid) {
  if (isEmpty(reportDiscardStrategy)) {
    return null;
  }
  const { appStrategyList, issueStrategyTotalCount } = reportDiscardStrategy;
  const errorAppStrategyList = (appStrategyList || []).filter(x => x.exceptionType === 'error');
  // issue级别的丢弃策略没有异常大类信息，目前就先全算到error上
  const hasErrorDiscardStrategy = errorAppStrategyList.length > 0 || issueStrategyTotalCount > 0;
  if (!hasErrorDiscardStrategy) {
    return null;
  }

  const appDom = errorAppStrategyList.length > 0 && <div>
    <div>{ ze('当前项目配置了全局上报数据丢弃策略：', 'This app has configured global report drop strategy:') }</div>
    { errorAppStrategyList.map((x, i) => {
      const { exceptionType, blockPercent } = x;
      const categoryLabel = ExceptionCategoryUtil.toI18n(exceptionType, pid);
      return <div key={i}>{ ze(`丢弃 ${blockPercent} % 比例的${categoryLabel}上报`, `Drop ${blockPercent} % ${categoryLabel} reports`) }</div>;
    }) }
  </div>;
  const issueDom = issueStrategyTotalCount > 0 && <div>
    <div>{ ze(`当前项目配置了 ${issueStrategyTotalCount} 条问题级上报数据丢弃策略。`, `This app has configured issue report drop strategy for ${issueStrategyTotalCount} issues.`) }</div>
  </div>;

  return <div>
    { appDom }
    { issueDom }
  </div>;
}

function getDashboardFilterEnabledCategoryList(dashboardFilterList) {
  return (dashboardFilterList || [])
    .filter(x => x.enabled)
    .map(x => x?.config?.exceptionCategoryList || [])
    .flat();
}

const vitalCategoryTooltip = <div>
  <div>{ ze('当前为合并统计崩溃和ANR的指标。', 'Current metrics are merged statistics of crash and ANR.') }</div>
  <div>{ ze('例如：合并设备崩溃率 = （发生崩溃和发生ANR的设备数去重） / （总活跃设备数）', 'For example: Merged device crash rate = (Unique devices with crash or ANR reported) / (Total active devices)') }</div>
</div>;

const SummaryTab = ({
  reduxState, appId,
  searchParams,
  pid, onClick, pageType, headDataType,
  realTimeAppendData: realTimeAppendDataBeforeToJs,
  reportDiscardStrategy,
}) => {
  const currentApp = useMemo(() => reduxState.app.get('current').toJS(), [reduxState.app.get('current')]);
  const { teamType, appName } = currentApp;

  const currentCategory = ExceptionCategoryUtil.unify(pageType);

  const isQueryVital = !!searchParams?.get('isQueryVital');

  const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
  //（仅支持ANR的端支持，例如安卓、鸿蒙）莉莉丝需求，是否开启“崩溃+ANR“合并的新指标。（界面上直接取代原来的崩溃指标，可以通过下拉切换原崩溃指标）
  const showVitalAsCrash = PlatformUtil.isAnrAvailable(pid) && !!serverAppSettings[ServerAppSettings.keys.enableVitalCategory] && isQueryVital;
  const enableOom = serverAppSettings[ServerAppSettings.keys.enableOom];
  const enableJank = serverAppSettings[ServerAppSettings.keys.enableJank];
  const enableHang = !!serverAppSettings[ServerAppSettings.keys.enableHang];
  const isEnableSubModuleId = !!serverAppSettings[ServerAppSettings.keys.enableSubModuleId];
  const defaultVmType = serverAppSettings[ServerAppSettings.keys.dashboardDefaultVmType] || VmTypeInt.ALL;

  const dashboardFilterList = reduxState.app.get('appIdToDashboardFilterList')[appId];
  const dashboardFilterEnabledCategoryList = getDashboardFilterEnabledCategoryList(dashboardFilterList) || [];

  const hasVmTypeCloudGaming = ProductSummaryUtil.useHasVmTypeCloudGaming();

  const { t } = useTranslation();
  const realTimeAppendData = realTimeAppendDataBeforeToJs.toJS();
  const {
    errorNumAllData,
    errorDeviceAllData,
    accessNum,
    accessUser,
  } = realTimeAppendData || {};

  // 王者定制需求，如果项目开启了subModuleId，则头部的卡顿指标固定只展示局外指标'LobbyProfilerEnable'，不随subModuleId变化而改变。 --story=129624165 【CS】【P0】【王者】top问题、搜索支持按照卡顿时长过滤。大盘头部卡顿指标固定展示局外指标（不随发行渠道字段切换而改变）
  const isWangZhe = teamType === BillingTeamType.TENCENT && appName?.startsWith('王者');
  const isUseWangZheJankSummaryData = isWangZhe && isEnableSubModuleId;
  const wangZheJankSummaryDataFromJs = reduxState.summary.get('wangZheJankSummaryData');
  const wangZheJankSummaryData = useMemo(() => {
    return wangZheJankSummaryDataFromJs ? wangZheJankSummaryDataFromJs.toJS() : undefined;
  }, [wangZheJankSummaryDataFromJs]);

  const categoryList = useMemo(() => {
    const hasCrash = PlatformUtil.isCrashAvailable(pid);
    const hasAnr = PlatformUtil.isAnrAvailable(pid) && !showVitalAsCrash;
    const hasError = PlatformUtil.isErrorAvailable(pid);
    const hasOom = PlatformUtil.isOomAvailable(pid);
    const hasJank = enableJank;
    const hasHang = enableHang;
    return [
      ...(hasCrash ? [ExceptionCategory.CRASH] : []),
      ...(hasAnr ? [ExceptionCategory.ANR] : []),
      ...(hasError ? [ExceptionCategory.ERROR] : []),
      ...(hasOom ? [ExceptionCategory.OOM] : []),
      ...(hasJank ? [ExceptionCategory.JANK] : []),
      ...(hasHang ? [ExceptionCategory.HANG] : []),
    ];
  }, [pid, enableJank, enableHang, showVitalAsCrash]);

  const isTooManyCategories = categoryList.length >= 5;

  useEffect(() => {
    reportEvent({
      action: EVENT_ACTIONS.EXPOSURE,
      tp1: `${pageType}曝光`,
    });
  }, [pageType]);


  function renderFieldByHeadDataType(exceptionCategory, headDataType, reportNum, reportDevices, accessNum, accessDevices) {
    if (headDataType === SummaryHeadDataType.linuxDefault) {
      if (ExceptionCategoryUtil.isCrash(exceptionCategory)) {
        headDataType = SummaryHeadDataType.crashNum;
      } else {
        headDataType = SummaryHeadDataType.userRate;
      }
    }

    switch (headDataType) {
      case SummaryHeadDataType.crashUser: {
        return formatEnNumber(reportDevices);
      }
      case SummaryHeadDataType.crashNum: {
        return formatEnNumber(reportNum);
      }
      case SummaryHeadDataType.userRate: {
        return `${formatCrashPercentageString(reportDevices, accessDevices)} %`;
      }
      case SummaryHeadDataType.sessionCrashRate: {
        return `${formatCrashPercentageString(reportNum, accessNum)} %`;
      }
      default: {
        return '-';
      }
    }
  }

  const errorGrayStrategyTooltip = makeErrorDiscardStrategyTooltip(reportDiscardStrategy, pid);
  const hasErrorGrayStrategy = isNotNullish(errorGrayStrategyTooltip);
  const errorGrayFieldValue = hasErrorGrayStrategy
    ? renderFieldByHeadDataType(ExceptionCategory.ERROR, headDataType, errorNumAllData, errorDeviceAllData, accessNum, accessUser)
    : '';

  const hasExtraInfo = hasErrorGrayStrategy || showVitalAsCrash || isUseWangZheJankSummaryData;

  function makeItemDom(exceptionCategory, headDataType, vm, isDisabled, isMainStats) {
    const isOom = ExceptionCategoryUtil.isOom(exceptionCategory);
    const isJank = ExceptionCategoryUtil.isJank(exceptionCategory);
    let filterVm = vm;

    if (headDataType === SummaryHeadDataType.linuxDefault) {
      if (ExceptionCategoryUtil.isCrash(exceptionCategory)) {
        headDataType = SummaryHeadDataType.crashNum;
      } else {
        headDataType = SummaryHeadDataType.userRate;
      }
    }

    // 王者定制需求，如果项目开启了subModuleId，则头部的卡顿指标固定只展示局外指标'LobbyProfilerEnable'，不随subModuleId变化而改变。 --story=129624165 【CS】【P0】【王者】top问题、搜索支持按照卡顿时长过滤。大盘头部卡顿指标固定展示局外指标（不随发行渠道字段切换而改变）
    const summaryData = (isUseWangZheJankSummaryData && isJank) ? wangZheJankSummaryData : realTimeAppendData;
    let { allVmTypesDataList = [] } = summaryData || {};

    const stat = allVmTypesDataList.find(x => x.vm === filterVm);
    if (isDisabled || isNullish(stat)) {
      return '-';
    }

    let denominator = 1;
    if (headDataType === SummaryHeadDataType.userRate) {
      denominator = stat.accessDevice;
    } else if (headDataType === SummaryHeadDataType.sessionCrashRate) {
      denominator = stat.accessNum;
    }

    const isPercentage = headDataType === SummaryHeadDataType.userRate || headDataType === SummaryHeadDataType.sessionCrashRate;
    let numerator = 0;

    if (ExceptionCategoryUtil.isCrash(exceptionCategory)) {
      if (headDataType === SummaryHeadDataType.crashNum) {
        numerator = showVitalAsCrash ? stat.vitalNum : stat.crashNum;
      } else if (headDataType === SummaryHeadDataType.sessionCrashRate) {
        numerator = showVitalAsCrash ? stat.vitalNum : (stat.crashSession || stat.crashNum); // 兼容移动端crashSession为0的情况
      } else if (headDataType === SummaryHeadDataType.crashUser || headDataType === SummaryHeadDataType.userRate) {
        numerator = showVitalAsCrash ? stat.vitalDevice : stat.crashDevice;
      }
    } else if (ExceptionCategoryUtil.isAnr(exceptionCategory)) {
      if (headDataType === SummaryHeadDataType.crashNum) {
        numerator = stat.anrNum;
      } else if (headDataType === SummaryHeadDataType.sessionCrashRate) {
        numerator = stat.anrSession || stat.anrNum; // 兼容移动端crashSession为0的情况
      } else if (headDataType === SummaryHeadDataType.crashUser || headDataType === SummaryHeadDataType.userRate) {
        numerator = stat.anrDevice;
      }
    } else if (ExceptionCategoryUtil.isError(exceptionCategory)) {
      if (headDataType === SummaryHeadDataType.crashNum) {
        numerator = stat.errorNum;
      } else if (headDataType === SummaryHeadDataType.sessionCrashRate) {
        numerator = stat.errorSession || stat.errorNum; // 兼容移动端crashSession为0的情况
      } else if (headDataType === SummaryHeadDataType.crashUser || headDataType === SummaryHeadDataType.userRate) {
        numerator = stat.errorDevice;
      }
    } else if (ExceptionCategoryUtil.isJank(exceptionCategory)) {
      if (headDataType === SummaryHeadDataType.crashNum || headDataType === SummaryHeadDataType.sessionCrashRate) {
        numerator = stat.jankNum;
      } else if (headDataType === SummaryHeadDataType.crashUser || headDataType === SummaryHeadDataType.userRate) {
        numerator = stat.jankDevice;
      }
    } else if (ExceptionCategoryUtil.isHang(exceptionCategory)) {
      if (headDataType === SummaryHeadDataType.crashNum || headDataType === SummaryHeadDataType.sessionCrashRate) {
        numerator = stat.hangNum;
      } else if (headDataType === SummaryHeadDataType.crashUser || headDataType === SummaryHeadDataType.userRate) {
        numerator = stat.hangDevice;
      }
    } else if (isOom) {
      if (headDataType === SummaryHeadDataType.crashNum) {
        numerator = stat.oomNum;
      } else if (headDataType === SummaryHeadDataType.sessionCrashRate) {
        numerator = stat.oomNum;
      } else if (headDataType === SummaryHeadDataType.crashUser || headDataType === SummaryHeadDataType.userRate) {
        numerator = stat.oomDevice;
      }
    }

    if (isPercentage) {
      const percent = formatCrashPercentageString(numerator, denominator);
      return isMainStats
        ? <div className={_style.percent}>{ percent }</div>
        : `${percent}\xA0%`;
    }
    if (isTooManyCategories && numerator >= 1_000_000) {
      return formatCompactNumber(numerator);
    } else {
      return formatEnNumber(numerator);
    }
  }

  return <ul className={_style.summary_tab}>
    {
      categoryList.map((x, index) => {
        const isOomAndDisabled = x === ExceptionCategory.OOM && !enableOom;
        const showVitalCategoryHint = ExceptionCategoryUtil.isCrash(x) && showVitalAsCrash;
        const showErrorGrayHint = ExceptionCategoryUtil.isError(x) && hasErrorGrayStrategy;
        const showWangZheJankHint = ExceptionCategoryUtil.isJank(x) && isUseWangZheJankSummaryData;

        const hasAnyHint = showVitalCategoryHint || showErrorGrayHint || showWangZheJankHint;

        const dom = <li
          className={classnames(_style.summary_tab_item,
            currentCategory === x && _style.active,
            isOomAndDisabled && _style.itemDisabled)}
          style={{color: isOomAndDisabled ? '#393157' : '' }}
          key={`${x}${index}`}
          onClick={() => {
            if (isOomAndDisabled) {
              return;
            }
            onClick(x);
          }}
          value={x}
        >
          { dashboardFilterEnabledCategoryList.includes(x) && <div className={_style.filterStrategyMark}>
            <Tooltip
              title={(<div>
                <span>{ze('项目大盘过滤策略已配置生效，','The project has enabled the filtering strategy, ')}</span>
                <Link
                  to={`/crash-reporting/preferences/dashboard-filters/${appId}?pid=${pid}`}
                  target='_blank'
                >
                  <span style={{color:'#6669FE'}}>{ze('前往查看','go to view')}</span>
                </Link>
              </div>)}
            >
              <div className={_style.filterStrategyMarkIcon}>
                <DashboardFilterIcon />
              </div>
            </Tooltip>

          </div> }
          <div className={_style.summaryTabMainRow}>
            <div className={_style.summary_tab_item_key}>{ ix(ExceptionCategoryUtil.toI18n(x, pid)) }</div>
            <div className={_style.summary_tab_item_value}>{makeItemDom(x, headDataType, defaultVmType, isOomAndDisabled, true)}</div>
            {defaultVmType !== VmTypeInt.ALL && <div className={_style.summary_tab_item_key} style={{ marginLeft: '8px'}}>
              {ze('（', '(')}{VmTypeUtil.toI18n(defaultVmType)}{ze('）', ')')}
            </div>}
          </div>
          { hasExtraInfo && <div
            className={` ${_style.summaryTabItemGray}`}
            style={{ visibility: hasAnyHint ? 'visible' : 'hidden' }}
          >
            { showVitalCategoryHint && <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div>{ ze(`统计崩溃和ANR合并数据`, `Stats of merged crash and ANR data`)}</div>
              <Tooltip
                overlayStyle={{minWidth: '360px'}}
                title={vitalCategoryTooltip}
                placement='top'
              >
                <WrappedTipsIcon/>
              </Tooltip>
            </div> }
            { showErrorGrayHint && <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div>{ze(`丢弃前：${errorGrayFieldValue}`, `Before Discarded: ${errorGrayFieldValue}`)}</div>
              <Tooltip
                overlayStyle={{minWidth: '360px'}}
                title={errorGrayStrategyTooltip}
                placement='top'
              >
                <WrappedTipsIcon/>
              </Tooltip>
            </div> }
            { showWangZheJankHint && <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div>{ ze(`（仅LobbyProfilerEnable数据）`, `(Only LobbyProfilerEnable Data)`)}</div>
            </div> }
            { !hasAnyHint && <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>------</div>}
          </div> }
          {
            PlatformUtil.isVmTypeSupported(pid) && <div className={isTooManyCategories ? _style.summary_tab_vm_pm_div_too_many_categories : _style.summary_tab_vm_pm_div}>
              { defaultVmType !== VmTypeInt.ALL && <div><span>{ze('全量', 'All')}:</span> <span>{makeItemDom(x, headDataType, VmTypeInt.ALL, isOomAndDisabled, false)}</span></div> }
              { defaultVmType !== VmTypeInt.REAL_DEVICE && <div><span>{t('REPORTDETAIL.真机')}:</span> <span>{makeItemDom(x, headDataType, VmTypeInt.REAL_DEVICE, isOomAndDisabled, false)}</span></div> }
              { defaultVmType !== VmTypeInt.EMULATOR && <div><span>{t('REPORTDETAIL.模拟器')}:</span> <span>{makeItemDom(x, headDataType, VmTypeInt.EMULATOR, isOomAndDisabled, false)}</span></div> }
              { defaultVmType !== VmTypeInt.CLOUD_GAMING && hasVmTypeCloudGaming && <div><span>{t('REPORTDETAIL.云游戏')}:</span> <span>{makeItemDom(x, headDataType, VmTypeInt.CLOUD_GAMING, isOomAndDisabled, false)}</span></div> }
            </div>
          }
        </li>;

        if (!isOomAndDisabled) {
          return dom;
        } else {
          return <Tooltip
            placement='bottom'
            title={t('EXCP_OVERVIEW.OOM未开启提示语')}
          >{ dom }</Tooltip>;
        }
      })
    }
  </ul>;
};

const mapStateToProps = (state, router) => ({
  reduxState: state,
});

SummaryTab.propTypes = {
  reduxState: PropTypes.object,
  realTimeAppendData: PropTypes.object,
  reportDiscardStrategy: PropTypes.object,
  appId: PropTypes.string,
  pid: PropTypes.number,
  onClick: PropTypes.func,
  pageType: PropTypes.string,
  headDataType: PropTypes.string,
};

export default connect(mapStateToProps)(SummaryTab);
